import { z } from 'zod';
import { MediaRefSchema } from './assetRef';
import { LayoutSchemaV1 } from './layoutSchema';
import { MinigameConfigSchema } from './minigameConfig';
import { VariableMutationSchema } from './variableSchema';
export const StepType = z.enum(['scene', 'dialog', 'ai_dialog', 'free_input', 'line_dialog', 'spot_gate', 'minigame', 'branch', 'reward', 'ending', 'variable_mutation']);
// NOTE: MediaRefSchema は assetRef.ts へ移設（NR4）。ポリシー正本は assetRef.ts / docs/prod/specs/asset-reference-spec.md。
// オーバーレイ要素（画像・テキストを自由配置）
const OverlayElementSchema = z.object({
    id: z.string().min(1),
    type: z.enum(['text', 'image']),
    xPct: z.number().min(-100).max(200),
    yPct: z.number().min(-100).max(200),
    widthPct: z.number().min(0).max(200),
    heightPct: z.number().min(0).max(200),
    content: z.string().optional(),
    style: z.object({
        fontSize: z.number().optional(),
        color: z.string().optional(),
        fontWeight: z.string().optional()
    }).optional(),
    assetId: z.string().optional(),
    layer: z.enum(['front', 'back']).optional() // 前面(default) or 背面（字幕より後ろ）
});
const StepBase = z.object({
    id: z.string().min(1),
    type: StepType,
    title: z.string().min(1).default(''),
    notes: z.string().optional().default(''),
    next: z.string().optional(),
    overlays: z.array(OverlayElementSchema).optional()
});
// ============================
// BranchPreset定義（他のStep型より先に定義）
// ============================
const BranchPresetAlways = z.object({
    type: z.literal('always')
});
const BranchPresetVariableEquals = z.object({
    type: z.literal('variable_equals'),
    key: z.string().min(1),
    value: z.union([z.string(), z.number(), z.boolean()])
});
const BranchPresetVariableGte = z.object({
    type: z.literal('variable_gte'),
    key: z.string().min(1),
    value: z.number()
});
const BranchPresetFlagEquals = z.object({
    type: z.literal('flag_equals'),
    key: z.string().min(1),
    value: z.union([z.string(), z.number(), z.boolean()])
});
const BranchPresetFlagTrue = z.object({
    type: z.literal('flag_is_true'),
    key: z.string().min(1)
});
// Phase A-h: 追加プリセット
const BranchPresetVariableLte = z.object({
    type: z.literal('variable_lte'),
    key: z.string().min(1),
    value: z.number()
});
const BranchPresetVariableLt = z.object({
    type: z.literal('variable_lt'),
    key: z.string().min(1),
    value: z.number()
});
const BranchPresetVariableGt = z.object({
    type: z.literal('variable_gt'),
    key: z.string().min(1),
    value: z.number()
});
const BranchPresetVariableIn = z.object({
    type: z.literal('variable_in'),
    key: z.string().min(1),
    values: z.array(z.union([z.string(), z.number(), z.boolean()]))
});
const BranchPresetFlagFalse = z.object({
    type: z.literal('flag_is_false'),
    key: z.string().min(1)
});
export const BranchPreset = z.discriminatedUnion('type', [
    BranchPresetAlways,
    BranchPresetVariableEquals,
    BranchPresetVariableGte,
    BranchPresetVariableLte,
    BranchPresetVariableLt,
    BranchPresetVariableGt,
    BranchPresetVariableIn,
    BranchPresetFlagEquals,
    BranchPresetFlagTrue,
    BranchPresetFlagFalse
]);
// ============================
// Step型定義
// ============================
const SceneStep = StepBase.extend({
    type: z.literal('scene'),
    layout: LayoutSchemaV1.optional(),
    config: z
        .object({
        imageOnly: z.boolean().optional(),
        confirmButton: z
            .object({
            label: z.string().default('確認して次へ'),
            position: z.enum(['top', 'bottom']).default('top')
        })
            .optional()
    })
        .optional(),
    bg: z.object({
        kind: z.enum(['image', 'video', 'color']).default('image'),
        src: MediaRefSchema.optional(),
        color: z.string().optional(),
        fit: z.enum(['cover', 'contain']).default('cover'),
        dimOpacity: z.number().min(0).max(1).default(0.2)
    }),
    bgm: z
        .object({
        src: MediaRefSchema.optional(),
        volume: z.number().min(0).max(1).default(0.8),
        loop: z.boolean().default(true)
    })
        .default({ volume: 0.8, loop: true })
});
const DialogStep = StepBase.extend({
    type: z.literal('dialog'),
    layout: LayoutSchemaV1.default({
        v: 1,
        templateId: 'dialog_overlay',
        params: {}
    }),
    // speakerKey -> side（left/right）の辞書。発話行のspeakerKeyから話者側を決定する。
    speakerSideMap: z.record(z.string(), z.enum(['left', 'right'])).optional(),
    // dialog背景（sceneとは別に、会話中の背景を明示的に設定できるようにする）
    bg: z
        .object({
        kind: z.enum(['image', 'color']).default('image'),
        src: MediaRefSchema.optional(),
        color: z.string().optional(),
        fit: z.enum(['cover', 'contain']).default('cover'),
        dimOpacity: z.number().min(0).max(1).default(0.3)
    })
        .optional(),
    // dialog中のBGM（必要なら会話中もBGMを鳴らす）
    bgm: z
        .object({
        src: MediaRefSchema.optional(),
        volume: z.number().min(0).max(1).default(0.8),
        loop: z.boolean().default(true),
        // 明示的に止めたい場合（src無しでも停止指示できる）
        stop: z.boolean().optional()
    })
        .optional(),
    speaker: z
        .object({
        name: z.string().default(''),
        nameColor: z.string().optional()
    })
        .default({ name: '' }),
    // 互換: 従来はtext単体。新仕様では lines（複数ラリー）も許可する。
    text: z.string().default(''),
    lines: z
        .array(z.object({
        // 表記揺れしない内部キー（speakerSideMapで左右に割り当て）
        speakerKey: z.string().min(1).optional(),
        // 明示指定（省略時は継承 or speakerKeyで決定）
        activePortraitSide: z.enum(['left', 'right', 'none']).optional(),
        speaker: z
            .object({
            name: z.string().default(''),
            nameColor: z.string().optional()
        })
            .default({ name: '' }),
        text: z.string().min(1),
        // 発話ごとの背景上書き（必要な箇所だけ指定）
        bg: z
            .object({
            kind: z.enum(['image', 'color']).default('image'),
            src: MediaRefSchema.optional(),
            color: z.string().optional(),
            fit: z.enum(['cover', 'contain']).default('cover'),
            dimOpacity: z.number().min(0).max(1).default(0.3)
        })
            .optional(),
        // 発話ごとの立ち絵/BGM差し替え（「区間だけ」は開始/終了に設定して戻せる）
        portraits: z
            .object({
            leftSrc: MediaRefSchema.optional(),
            rightSrc: MediaRefSchema.optional(),
            hideLeft: z.boolean().optional(),
            hideRight: z.boolean().optional()
        })
            .optional(),
        resetPortraits: z.boolean().optional(),
        bgm: z
            .object({
            src: MediaRefSchema.optional(),
            volume: z.number().min(0).max(1).default(0.8),
            loop: z.boolean().default(true),
            stop: z.boolean().optional()
        })
            .optional(),
        resetBgm: z.boolean().optional(),
        // P7-2: このラインだけウインドウの「顔」を上書きする（省略時はStep直下のface→既定'subtitle'に継承）。
        // 'subtitle' = 枠なし字幕型ミニマル（既定）。'antique' = 骨董・和風枠（付喪神が語る等、異質な割り込みの演出装置）。
        face: z.enum(['subtitle', 'antique']).optional()
    }))
        .min(1)
        .optional(),
    sfx: MediaRefSchema.optional(),
    portraits: z
        .object({
        leftSrc: MediaRefSchema.optional(),
        rightSrc: MediaRefSchema.optional()
    })
        .default({}),
    portraitPositions: z
        .object({
        left: z
            .object({
            xPct: z.number().min(0).max(100).default(25),
            yPct: z.number().min(0).max(100).default(55),
            // 互換: 旧scale（固定サイズに対する倍率）
            scale: z.number().min(0.2).max(2.5).default(1),
            // 新: 自由配置のためのサイズ（キャンバス比 %）
            widthPct: z.number().min(1).max(100).default(40),
            heightPct: z.number().min(1).max(100).default(60),
            // 横長画面だけ全身を収めたい立ち絵向け。既定の cover は互換維持。
            fit: z.enum(['cover', 'contain', 'contain_landscape']).optional(),
            // 新: トリミング（object-position）
            cropXPct: z.number().min(0).max(100).default(50),
            cropYPct: z.number().min(0).max(100).default(35)
        })
            .optional(),
        right: z
            .object({
            xPct: z.number().min(0).max(100).default(75),
            yPct: z.number().min(0).max(100).default(55),
            scale: z.number().min(0.2).max(2.5).default(1),
            widthPct: z.number().min(1).max(100).default(40),
            heightPct: z.number().min(1).max(100).default(60),
            fit: z.enum(['cover', 'contain', 'contain_landscape']).optional(),
            cropXPct: z.number().min(0).max(100).default(50),
            cropYPct: z.number().min(0).max(100).default(35)
        })
            .optional()
    })
        .optional(),
    // Phase A-h: 選択肢（選んだ時に変数変更が可能）
    choices: z
        .array(z.object({
        /** 選択肢のテキスト */
        text: z.string().min(1),
        /** 選択時の遷移先ノードID */
        goto: z.string().min(1).optional(),
        /** 選択時に適用する変数変更 */
        mutations: z.array(VariableMutationSchema).optional(),
        /** 選択肢の表示条件（オプション） */
        condition: BranchPreset.optional(),
        /** 選択後に表示されなくなるか */
        hideAfterSelect: z.boolean().default(false)
    }))
        .optional(),
    // P7-1: 作者 opt-in の自動送り。line_dialog の同名ペア（:730-731）と命名・値域を統一する。
    // dialog は line_dialog と異なり literal object（.default() ではなく）で組み立てられる箇所が
    // 複数あるため、TS 側で「未指定なら required」を強制しない optional() を採る（振る舞いは同じ＝
    // 未指定時は autoAdvance=false 相当として読み手が `?? false` で扱う。既定は undefined＝
    // 全既存シナリオの挙動は不変（手動送りのまま））。
    autoAdvance: z.boolean().optional(),
    autoAdvanceDelayMs: z.number().int().min(0).max(10000).optional(),
    // P7-2: Step直下（lines未使用時、または全lineの既定値）のウインドウの「顔」。
    // 各line.faceが指定されていればそちらが優先。両方未指定なら既定'subtitle'。
    face: z.enum(['subtitle', 'antique']).optional()
});
// Phase A-h: DialogChoice型をエクスポート
export const DialogChoiceSchema = z.object({
    text: z.string().min(1),
    goto: z.string().min(1).optional(),
    mutations: z.array(VariableMutationSchema).optional(),
    condition: BranchPreset.optional(),
    hideAfterSelect: z.boolean().default(false)
});
const AiDialogStep = StepBase.extend({
    type: z.literal('ai_dialog'),
    layout: LayoutSchemaV1.default({
        v: 1,
        templateId: 'dialog_overlay',
        params: {}
    }),
    systemPrompt: z.string().default(''),
    userPrompt: z.string().default(''),
    maxTokens: z.number().int().min(32).max(512).default(180),
    temperature: z.number().min(0).max(1.5).default(0.7),
    // P7-2: ai_dialogにはlinesが無いため、Step直下のみでウインドウの「顔」を指定する。
    // 既定'subtitle'（枠なし字幕型ミニマル）。
    face: z.enum(['subtitle', 'antique']).optional()
});
const FreeInputRoute = z.object({
    routeKey: z.string().min(1),
    label: z.string().optional()
});
const FreeInputStep = StepBase.extend({
    type: z.literal('free_input'),
    layout: LayoutSchemaV1.default({
        v: 1,
        templateId: 'dialog_overlay',
        params: {}
    }),
    // シナリオデフォルトprompt（scenario.meta等）を使う場合は未指定でOK
    promptText: z.string().optional(),
    inputPlaceholder: z.string().optional(),
    maxChars: z.number().int().min(1).max(1000).default(200),
    llmTimeoutMs: z.number().int().min(1000).max(60000).default(15000),
    routes: z
        .array(FreeInputRoute)
        .min(1)
        .default([
        { routeKey: 'default', label: '通常' },
        { routeKey: 'insight', label: '鋭い指摘' },
        { routeKey: 'derail', label: '脱線/想定外' }
    ]),
    defaultRouteKey: z.string().min(1).default('default'),
    emptyRouteKey: z.string().min(1).default('no_input'),
    routeKeyFlagKey: z.string().min(1).default('routeKey'),
    // 返答の話者（固定）。ズーム対象もここで指定する（speakerKey / activePortraitSide のどちらでもOK）
    speaker: z
        .object({
        name: z.string().default(''),
        nameColor: z.string().optional()
    })
        .default({ name: '' }),
    speakerKey: z.string().min(1).optional(),
    activePortraitSide: z.enum(['left', 'right', 'none']).optional(),
    speakerSideMap: z.record(z.string(), z.enum(['left', 'right'])).optional(),
    // 返答は1〜N発話（Nは最大3を想定）
    bridgeMaxTurns: z.number().int().min(1).max(5).default(3),
    // 立ち絵（dialogと同じ形で流用）
    portraits: z
        .object({
        leftSrc: MediaRefSchema.optional(),
        rightSrc: MediaRefSchema.optional()
    })
        .default({}),
    portraitPositions: z
        .object({
        left: z
            .object({
            xPct: z.number().min(0).max(100).default(25),
            yPct: z.number().min(0).max(100).default(55),
            scale: z.number().min(0.2).max(2.5).default(1),
            widthPct: z.number().min(1).max(100).default(40),
            heightPct: z.number().min(1).max(100).default(60),
            fit: z.enum(['cover', 'contain', 'contain_landscape']).optional(),
            cropXPct: z.number().min(0).max(100).default(50),
            cropYPct: z.number().min(0).max(100).default(35)
        })
            .optional(),
        right: z
            .object({
            xPct: z.number().min(0).max(100).default(75),
            yPct: z.number().min(0).max(100).default(55),
            scale: z.number().min(0.2).max(2.5).default(1),
            widthPct: z.number().min(1).max(100).default(40),
            heightPct: z.number().min(1).max(100).default(60),
            fit: z.enum(['cover', 'contain', 'contain_landscape']).optional(),
            cropXPct: z.number().min(0).max(100).default(50),
            cropYPct: z.number().min(0).max(100).default(35)
        })
            .optional()
    })
        .optional()
});
const MinigameTemplateId = z.number().int().min(1).max(20);
const SpotGateNormalizeSchema = z
    .object({
    trim: z.boolean().default(true),
    kana: z.boolean().default(false),
    caseInsensitive: z.boolean().default(true),
    removeSpaces: z.boolean().default(true)
})
    .default({
    trim: true,
    kana: false,
    caseInsensitive: true,
    removeSpaces: true
});
const SpotGateStep = StepBase.extend({
    type: z.literal('spot_gate'),
    spotId: z.string().optional(),
    title: z.string().min(1),
    prompt: z.string().min(1),
    inputMode: z.enum(['keyword', 'observation', 'either']).default('keyword'),
    expectedAnswers: z.array(z.string().min(1)).min(1),
    matchMode: z.enum(['exact', 'contains']).optional(),
    normalize: SpotGateNormalizeSchema.optional(),
    maxAttempts: z.number().int().min(1).max(20).optional(),
    hints: z.array(z.string()).default([]),
    successMutations: z.array(VariableMutationSchema).optional(),
    failMutations: z.array(VariableMutationSchema).optional()
});
const MinigameResultMappingSchema = z.object({
    id: z.string().min(1),
    when: z
        .object({
        status: z.enum(['success', 'fail', 'partial']).optional(),
        minScore: z.number().optional(),
        resultKey: z.string().min(1).optional(),
        equals: z.union([z.string(), z.number(), z.boolean()]).optional()
    })
        .default({}),
    unlockClues: z.array(z.string().min(1)).optional(),
    setVariables: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    addItems: z.array(z.string().min(1)).optional(),
    next: z.string().min(1).optional()
});
const MinigameStep = StepBase.extend({
    type: z.literal('minigame'),
    layout: LayoutSchemaV1.default({
        v: 1,
        templateId: 'fullscreen',
        params: {}
    }),
    templateId: MinigameTemplateId,
    registryId: z.string().min(1).optional(),
    config: MinigameConfigSchema.default({}),
    resultVarKey: z.string().default('lastMinigameResult'),
    difficulty: z.enum(['easy', 'normal', 'hard']).default('normal'),
    timeLimitMs: z.number().int().min(0).max(600000).default(0),
    maxMistakes: z.number().int().min(0).max(99).default(0),
    ui: z
        .object({
        showTutorial: z.boolean().default(true)
    })
        .default({}),
    resultMappings: z.array(MinigameResultMappingSchema).optional()
});
const BranchRule = z.object({
    if: z.string().min(1).default('true'),
    goto: z.string().min(1),
    label: z.string().optional(),
    preset: BranchPreset.optional()
});
const BranchStep = StepBase.extend({
    type: z.literal('branch'),
    rules: z.array(BranchRule).min(1),
    fallbackGoto: z.string().min(1)
});
// Phase A-h: エンディング条件スキーマ
export const EndingConditionSchema = z.object({
    /** 条件（BranchPresetを使用） */
    condition: BranchPreset,
    /** 到達するエンディングID */
    endingId: z.string().min(1),
    /** エンディングのタイトル（表示用） */
    title: z.string().optional()
});
const RewardStep = StepBase.extend({
    type: z.literal('reward'),
    templateKey: z.string().min(1),
    inputs: z.record(z.string(), z.any()).default({}),
    nextAction: z.enum(['replay', 'next', 'end']).default('end'),
    resultType: z.enum(['success', 'partial', 'fail']).default('success'),
    stats: z
        .object({
        accuracy: z.number().min(0).max(100).optional(),
        hints: z.number().int().min(0).max(99).optional(),
        mistakes: z.number().int().min(0).max(99).optional()
    })
        .default({}),
    // Phase A-h: 変数に基づくエンディング条件
    endingConditions: z.array(EndingConditionSchema).optional(),
    defaultEnding: z.object({
        endingId: z.string().min(1),
        title: z.string().optional()
    }).optional()
});
// エンディングStep: シナリオ完了時の達成画面
const EndingStep = StepBase.extend({
    type: z.literal('ending'),
    // エンディング識別（マルチエンディング用）
    endingId: z.string().min(1),
    endingTitle: z.string().min(1),
    endingSubtitle: z.string().optional(),
    endingType: z.enum(['good', 'normal', 'bad', 'true', 'secret', 'comedy']).optional(),
    // 表示コンテンツ
    message: z.string().optional(),
    imageAssetId: MediaRefSchema.optional(),
    // 没入 P5「声を取り戻した付喪神図鑑」: このエンディング到達で端末コレクションに
    // 記録される付喪神のメタ。optional 追加のみ＝凍結非破壊（schemaVersion 1 維持・
    // 既存シナリオの parse は不変）。未設定のエンディングは図鑑へ記録しない（従来動作）。
    tsukumogamiData: z
        .object({
        name: z.string().min(1), // 例「そま（火）」
        description: z.string().optional(), // 一文の説明
        illustrationAssetId: z.string().optional(),
        rarity: z.enum(['common', 'uncommon', 'rare', 'legendary']).optional()
    })
        .optional(),
    // 達成情報表示
    showStats: z.boolean().default(true),
    stats: z
        .object({
        showPlayTime: z.boolean().default(true),
        showCollectedItems: z.boolean().default(false),
        showCorrectAnswers: z.boolean().default(false)
    })
        .optional(),
    // 演出
    bgmAssetId: MediaRefSchema.optional(),
    animation: z.enum(['fade', 'slide', 'none']).default('fade'),
    animationDurationMs: z.number().int().min(0).max(5000).default(1500),
    // 導線
    showReplayButton: z.boolean().default(true),
    showShareButton: z.boolean().default(false),
    customButtons: z
        .array(z.object({
        label: z.string().min(1),
        action: z.enum(['replay', 'home', 'url']),
        url: z.string().url().optional()
    }))
        .optional(),
    // コメディエンディング設定（オプション）
    comedyMode: z.object({
        enabled: z.boolean().default(false),
        primaryButton: z.object({
            label: z.string().default('再度ゲームを開始する'),
            action: z.enum(['replay', 'home', 'url']).default('replay'),
            url: z.string().url().optional(),
            sizeFactor: z.number().min(1).max(5).default(3),
            animation: z.enum(['none', 'pulse', 'bounce', 'shake']).default('pulse')
        }).default({}),
        secondaryButton: z.object({
            label: z.string().default('本当に終了する'),
            action: z.enum(['close', 'home', 'url']).default('close'),
            url: z.string().url().optional(),
            sizeFactor: z.number().min(0.3).max(1).default(0.5),
            confirmation: z.object({
                enabled: z.boolean().default(true),
                message: z.string().default('本当に？もうこれ押したら本当に終わるけどいいのね？'),
                confirmLabel: z.string().default('本当に終了する'),
                cancelLabel: z.string().default('やっぱりやめる')
            }).default({})
        }).default({}),
        extras: z.object({
            runawayButton: z.boolean().default(false),
            shakeOnConfirm: z.boolean().default(false),
            extraConfirmMessage: z.string().optional()
        }).default({})
    }).optional()
});
// 変数操作Step: 変数を直接変更する（Phase A-h で実装予定）
const VariableMutationStep = StepBase.extend({
    type: z.literal('variable_mutation'),
    // 適用する操作のリスト
    mutations: z.array(VariableMutationSchema).min(1),
    // 操作後のフィードバック表示（オプション）
    feedback: z
        .object({
        showToast: z.boolean().default(false),
        toastMessage: z.string().optional(),
        showAnimation: z.boolean().default(false)
    })
        .optional()
});
// ============================
// LINE Dialog Step: LINE風メッセージUI
// ============================
// Phase A-k: メッセージ種別を拡張（画像・動画・音声・位置情報・スタンプ・タイムスタンプ）
const LineMessageKind = z.enum([
    'text', // テキストメッセージ
    'image', // 画像メッセージ
    'video', // 動画メッセージ（Phase A-k 新設）
    'audio', // 音声メッセージ（Phase A-k 新設）
    'stamp', // スタンプ
    'location', // 位置情報共有（Phase A-k 新設）
    'timestamp', // タイムスタンプ区切り（Phase A-k 新設）
]);
// Phase A-k: 動画トラップ演出（動画再生時に超常演出を発動）
const VideoTrapEffectSchema = z.object({
    /** 発動タイミング: 再生開始 / 再生N秒後 / 再生完了時 */
    trigger: z.enum(['on_play', 'at_time', 'on_end']).optional(),
    /** at_time の場合の発動秒数 */
    triggerAtSec: z.number().min(0).max(300).optional(),
    /** 発動する超常演出ID */
    effectId: z.string().min(1),
    /** 演出パラメータ */
    params: z.record(z.string(), z.unknown()).optional()
});
const LineMessageSchema = z.object({
    senderKey: z.string().min(1),
    /** テキスト内容（text/timestamp では必須、他kindでは説明文やキャプション） */
    text: z.string().min(1),
    delayMs: z.number().int().min(0).max(10000).default(800),
    typingIndicatorMs: z.number().int().min(0).max(5000).default(1500),
    kind: LineMessageKind.default('text'),
    // --- メディア系フィールド ---
    /** 画像/スタンプ/動画サムネイルのアセットID or URL */
    imageAssetId: MediaRefSchema.optional(),
    /** 動画ファイルのアセットID or URL（kind: 'video'） */
    videoAssetId: MediaRefSchema.optional(),
    /** 音声ファイルのアセットID or URL（kind: 'audio'） */
    audioAssetId: MediaRefSchema.optional(),
    /** 音声の表示用再生時間（秒）。省略時は自動検出 */
    audioDurationSec: z.number().min(0).max(600).optional(),
    // --- 位置情報フィールド（kind: 'location'） ---
    location: z.object({
        name: z.string().min(1),
        address: z.string().optional(),
        lat: z.number().optional(),
        lng: z.number().optional()
    }).optional(),
    // --- Phase A-k: 既読表示 ---
    /** 既読マーク表示（side: 'self' のメッセージで有効） */
    showRead: z.boolean().optional(),
    /** 既読表示の遅延（ms）。0=即座に表示 */
    readDelayMs: z.number().int().min(0).max(30000).optional(),
    /**
     * 没入 P4-2: 深夜帯（端末ローカル 23-4 時）のみ「既読だけついて返事が来ない」不穏演出。
     * true かつ再生時が深夜のとき、このメッセージは既読（showRead 準拠）はつくが以降の返信/
     * タイピングを保留する。深夜でなければ通常動作（保留なし）。optional 追加のみ＝凍結非破壊。
     */
    deepNightReadHold: z.boolean().optional(),
    // --- Phase A-k: メッセージ取り消し ---
    /** 表示後にメッセージを取り消す演出 */
    recallAfterMs: z.number().int().min(0).max(30000).optional(),
    /** 取り消し後の表示テキスト（省略時: 'メッセージの送信が取り消されました'） */
    recallDisplayText: z.string().optional(),
    // --- Phase A-k: 動画トラップ演出 ---
    videoTrapEffect: VideoTrapEffectSchema.optional(),
    // --- 超常演出（既存） ---
    supernaturalEffect: z.object({
        effectId: z.string().min(1),
        trigger: z.enum(['on_appear', 'after_delay', 'on_scroll']).default('on_appear'),
        triggerDelayMs: z.number().int().min(0).max(10000).default(0),
        params: z.record(z.string(), z.unknown()).default({})
    }).optional()
});
const LineReplyOptionSchema = z.object({
    text: z.string().min(1),
    goto: z.string().optional(),
    mutations: z.array(VariableMutationSchema).optional(),
    condition: BranchPreset.optional()
});
const LineSpeakerSchema = z.object({
    displayName: z.string().min(1),
    profileImage: MediaRefSchema.optional(),
    bubbleColor: z.string().optional(),
    bubbleTextColor: z.string().optional(),
    side: z.enum(['other', 'self']).default('other')
});
const LineDialogStep = StepBase.extend({
    type: z.literal('line_dialog'),
    chatTitle: z.string().min(1),
    chatProfileImage: MediaRefSchema.optional(),
    speakers: z.record(z.string(), LineSpeakerSchema),
    messages: z.array(LineMessageSchema).min(1),
    reply: z.object({
        mode: z.enum(['choices', 'choices_and_free_input']).default('choices'),
        options: z.array(LineReplyOptionSchema).min(1),
        freeInput: z.object({
            placeholder: z.string().default('メッセージを入力...'),
            maxChars: z.number().int().min(1).max(500).default(200),
            systemPrompt: z.string().optional(),
            followUpMessages: z.array(LineMessageSchema).optional(),
            routes: z.array(z.object({
                routeKey: z.string().min(1),
                label: z.string().optional()
            })).optional(),
            defaultRouteKey: z.string().default('default')
        }).optional()
    }).optional(),
    autoAdvance: z.boolean().default(false),
    autoAdvanceDelayMs: z.number().int().min(0).max(10000).default(2000),
    theme: z.object({
        headerColor: z.string().default('#06C755'),
        chatBgColor: z.string().default('#8CAAB9'),
        chatBgPattern: z.enum(['none', 'line_default', 'dark']).default('line_default')
    }).default({}),
    /**
     * 没入 P7-4（minor・optional追加のみ・凍結非破壊）: 表示形態。
     * 未指定 or 'fullscreen' = 現行の全画面 LINE（従来どおり1pxも変えない）。
     * 'panel' = 「その場でスマホを開く」— 直前の scene 背景が見え続けたまま、
     * 右下 FAB タップで画面右側 ~76% に LINE パネルが slide-in する。
     * 正本: docs/prod/shell_immersion_proposal_2026-07-02.md §P7-4
     */
    presentation: z.enum(['fullscreen', 'panel']).optional()
});
export const StepSchema = z.discriminatedUnion('type', [
    SceneStep,
    DialogStep,
    AiDialogStep,
    FreeInputStep,
    LineDialogStep,
    SpotGateStep,
    MinigameStep,
    BranchStep,
    RewardStep,
    EndingStep,
    VariableMutationStep
]);
//# sourceMappingURL=stepSchema.js.map